import { useCallback, useContext, useMemo, useState } from "react"
import SearchBar from "./SearchBar"
import { useTranslation } from "react-i18next"
import { ChatCard } from "./ui/chat-card"
import { addBookmark } from "../api/xeps/PepNativeBookmarks"
import { XmppContext } from "../stream/xmppClient"
import utilsCss from "./utilsCss.module.css"
import { useAppSelector, useAppStore } from "../reducers/hooks"
import { joinChat } from "../reducers/chatsSliceThunks"
import { selectSimpleChats } from "../reducers/chatsSliceSelectors"
import ChatHelpers from "../lib/chatHelpers"
import { selectSeenChatJids } from "../reducers/seenChatsSelectors"
import { addToSeenChats } from "../reducers/seenChatsSlice"
import { selectAccountChatsAsList } from "../reducers/accountChatsSlice"
import { AccountChat } from "../lib/accountChatsParser"
import { getCarIssuesJid, getSlickshiftAIJid } from "../jidUtils"
import { selectUserChats } from "@/reducers/userChatsSlice"
import { SquarePen } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { useFeatureFlagEnabled } from "posthog-js/react"
import {
  findTranschatUserChat,
  getChatDisplayName,
  isSlickshiftUser,
  shouldHideTranschatChat,
} from "@/lib/privateAgentUtils"

type JoinChatPopoverProps = {
  onActiveChatChange: (chatJid: string) => void
}

const JoinChatPopover = ({ onActiveChatChange }: JoinChatPopoverProps) => {
  const { t } = useTranslation()
  const { client, myJid } = useContext(XmppContext)
  const store = useAppStore()
  const chats = useAppSelector(selectSimpleChats)
  const seenJids: string[] = useAppSelector(selectSeenChatJids)
  const accountChats = useAppSelector(selectAccountChatsAsList)
  const userChats = useAppSelector(selectUserChats)
  const userChatsEnabled = Boolean(useFeatureFlagEnabled("private-agent"))
  const assistantChatJid = getSlickshiftAIJid(myJid)
  const carIssuesJid = getCarIssuesJid(myJid)
  const isSlickshiftLoggedUser = isSlickshiftUser(myJid.local)
  const ownTranschatJid = useMemo(
    () => findTranschatUserChat(userChats, myJid.local)?.jid,
    [userChats, myJid.local],
  )

  const [searchValue, setSearchValue] = useState<string>("")

  const chatOptions = useMemo(() => {
    const all = accountChats.filter(
      (chat) =>
        !chats.some(({ jid }) => jid === chat.jid) &&
        chat.jid !== assistantChatJid &&
        chat.jid !== carIssuesJid &&
        !chat.hidden &&
        (userChatsEnabled || !userChats[chat.jid]) &&
        !shouldHideTranschatChat({
          privateAgentEnabled: userChatsEnabled,
          isSlickshiftUser: isSlickshiftLoggedUser,
          chatJid: chat.jid,
          ownTranschatJid,
        }),
    )
    const allWithDisplayName = all.map((chat) => ({
      ...chat,
      name: getChatDisplayName({
        chatName: chat.name,
        chatJid: chat.jid,
        isSlickshiftUser: isSlickshiftLoggedUser,
      }),
    }))
    const filtered = allWithDisplayName.filter((chat) =>
      chat.name.toLowerCase().includes(searchValue.toLowerCase()),
    )

    return { all: allWithDisplayName, filtered }
  }, [
    chats,
    accountChats,
    assistantChatJid,
    carIssuesJid,
    searchValue,
    userChatsEnabled,
    userChats,
    isSlickshiftLoggedUser,
    ownTranschatJid,
  ])

  const onChatSelect = useCallback(
    async (chat: AccountChat) => {
      if (!userChatsEnabled && userChats[chat.jid]) return

      setSearchValue("")
      await addBookmark(client, chat.jid, chat.name)
      window.analytics.track("AddBookmark", {
        chatJid: chat?.jid,
        chatName: chat?.name,
        from: "JoinChatPopover",
      })
      store.dispatch(joinChat(client, chat))
      onActiveChatChange(chat.jid)
    },
    [client, store, onActiveChatChange, userChatsEnabled, userChats],
  )

  const shouldDisplayJoinChat = chatOptions.all.length > 0
  const shouldDisplaySearchBar = chatOptions.all.length > 5
  const newChats = useMemo(() => {
    if (!seenJids || seenJids.length === 0) return []

    return chatOptions.all.filter((chat) => !seenJids.includes(chat.jid))
  }, [seenJids, chatOptions.all])

  const newChatsFirstSortFn = useCallback(
    (a: AccountChat, b: AccountChat) => {
      if (newChats.includes(a)) return -1
      if (newChats.includes(b)) return 1
      return a.name.localeCompare(b.name)
    },
    [newChats],
  )

  const markAllChatsAsSeen = useCallback(() => {
    store.dispatch(addToSeenChats(newChats.map((chat) => chat.jid)))
  }, [store, newChats])

  const ChatOptions = () => {
    if (chatOptions.filtered.length === 0) {
      return (
        <div className={utilsCss.noResults}>{t("app.chatList.noResults")}</div>
      )
    }

    return (
      <>
        {chatOptions.filtered.sort(newChatsFirstSortFn).map((chat) => (
          <ChatCard
            key={chat.jid}
            name={chat.name}
            avatarName={chat.name}
            avatarPhoto={undefined}
            avatarColor={
              ChatHelpers.getAccountChatColor(chat) ||
              "var(--defaultAvatarColor)"
            }
            compact={true}
            onClick={() => onChatSelect(chat)}
          />
        ))}
      </>
    )
  }

  return (
    <Popover onOpenChange={(open) => !open && markAllChatsAsSeen()}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          style={{ display: shouldDisplayJoinChat ? "flex" : "none" }}
          className="relative"
        >
          <SquarePen />
          {newChats.length > 0 && (
            <div className="absolute right-2 top-2 h-2 w-2 -translate-y-1/2 translate-x-1/2 transform rounded-full bg-base-primary" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        className="max-h-[520px] min-w-[200px] overflow-y-auto p-0"
      >
        {shouldDisplaySearchBar && (
          <div className="sticky top-0 z-10 border-b bg-background p-3">
            <SearchBar
              value={searchValue}
              onValueChange={setSearchValue}
              inPopover
            />
          </div>
        )}
        <div className="p-2">
          <ChatOptions />
        </div>
      </PopoverContent>
    </Popover>
  )
}

export default JoinChatPopover
